import request from '../../utils/request';
import { Result } from '@/models/general';
import {
  RefereeView,
  RefereeListView,
  RefereeListQuery,
  RefereeRecommendationView,
  RefereeScheduleView,
  AssignmentHistoryView,
  AssignmentResultView,
  AssignmentResponseResultView,
  RespondToAssignmentInput,
  AssignRefereeInput,
  CreateRefereeProfileInput,
  UpdateRefereeProfileInput,
  MyAssignmentsView,
  MyAssignmentsQuery,
  MyWorkPointsSummaryView,
  RefereeAvailability,
} from '@/models/tournament';

export async function getReferees(query: RefereeListQuery) {
  const response = await request.get<Result<RefereeListView>>('Referee/GetReferees', { params: query });
  return response.data;
}

export async function getReferee(id: number) {
  const response = await request.get<Result<RefereeView>>(`Referee/GetReferee/${id}`);
  return response.data;
}

export async function createRefereeProfile(input: CreateRefereeProfileInput) {
  const response = await request.post<Result<RefereeView>>('Referee/CreateRefereeProfile', input);
  return response.data;
}

export async function updateRefereeProfile(input: UpdateRefereeProfileInput) {
  const response = await request.put<Result<RefereeView>>('Referee/UpdateRefereeProfile', input);
  return response.data;
}

export async function assignReferee(input: AssignRefereeInput) {
  const response = await request.post<Result<AssignmentResultView>>('Referee/AssignReferee', input);
  return response.data;
}

export async function getRecommendedReferees(matchId: number) {
  const response = await request.get<Result<RefereeRecommendationView[]>>(`Referee/GetRecommendedReferees/${matchId}`);
  return response.data;
}

export async function getRefereeSchedule(id: number, startDate?: string, endDate?: string) {
  const response = await request.get<Result<RefereeScheduleView[]>>(`Referee/GetRefereeSchedule/${id}`, {
    params: { startDate, endDate },
  });
  return response.data;
}

export async function getAssignmentHistory(matchId: number) {
  const response = await request.get<Result<AssignmentHistoryView[]>>(`Referee/GetAssignmentHistory/${matchId}`);
  return response.data;
}

export async function getMyAssignments(query?: MyAssignmentsQuery) {
  const response = await request.get<Result<MyAssignmentsView>>('Referee/GetMyAssignments', { params: query });
  return response.data;
}

export async function getMySchedule(startDate?: string, endDate?: string) {
  const response = await request.get<Result<RefereeScheduleView[]>>('Referee/GetMySchedule', {
    params: { startDate, endDate },
  });
  return response.data;
}

export async function getMyWorkPoints(query?: { startDate?: string; endDate?: string }) {
  const response = await request.get<Result<MyWorkPointsSummaryView>>('Referee/GetMyWorkPoints', { params: query });
  return response.data;
}

export async function getMyProfile() {
  const response = await request.get<Result<RefereeView>>('Referee/GetMyProfile');
  return response.data;
}

export interface AvailabilityChange {
  date: string;
  status: RefereeAvailability;
}

export async function setMyAvailability(changes: AvailabilityChange[]) {
  const response = await request.post<Result<RefereeScheduleView[]>>('Referee/SetMyAvailability', { changes });
  return response.data;
}

export async function acceptAssignment(input: RespondToAssignmentInput) {
  const response = await request.post<Result<AssignmentResponseResultView>>('Referee/AcceptAssignment', input);
  return response.data;
}

export async function declineAssignment(input: RespondToAssignmentInput) {
  const response = await request.post<Result<AssignmentResponseResultView>>('Referee/DeclineAssignment', input);
  return response.data;
}

export interface CompanyUser {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
}

// Raw shape of UserView as returned by GET /Account/Users
interface RawCompanyUser {
  id: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
}

export async function getCompanyUsers(): Promise<Result<CompanyUser[]>> {
  const response = await request.get<Result<RawCompanyUser[]>>('Account/Users', {
    params: { scope: 'Company', id: 0 },
  });
  const { data } = response;
  const mapped: CompanyUser[] = (data.payload ?? []).map((u) => {
    const derivedName =
      (u.fullName && u.fullName.trim()) ||
      [u.firstName, u.lastName].filter(Boolean).join(' ').trim() ||
      u.email ||
      '';
    return {
      id: u.id,
      name: derivedName,
      email: u.email,
      phone: u.phoneNumber,
    };
  });
  return { ...data, payload: mapped } as Result<CompanyUser[]>;
}
